import React from 'react'

import { Box } from '@features/story/components/Box'
import { abbreviateNumber, addComma } from '@lib/utils'
import { Button } from '@components/Buttons'
import { SocialSharing } from '@components/SocialSharing'
import { AddIcon } from '@components/icons/AddIcon'
import { ChatboxIcon } from '@components/icons/ChatboxIcon'
import { CheckIcon } from '@components/icons/CheckIcon'
import { EBookIcon } from '@components/icons/EBookIcon'
import { EyeIcon } from '@components/icons/EyeIcon'
import { HeartIcon } from '@components/icons/HeartIcon'
import { HeartSolidIcon } from '@components/icons/HeartSolidIcon'
import { ListIcon } from '@components/icons/ListIcon'
import { useDebounce } from '@hooks/useDebounce'
import { SHOW_EBOOK_FEATURE } from '@configs/config'

interface CoverMenuSectionProps {
  id: number
  shareUrl: string
  chapterTotal: number
  viewCount: number
  likeCount: number
  commentCount: number
  isLiked: boolean
  isEpub: boolean
  isPdf: boolean
  isBookOnShelf: boolean
  isBookShelfActionLoading: boolean
  isLodingToggleBookLike: boolean
  readNowHandler: () => Promise<void>
  handleLikeBook: () => Promise<void>
  handleUpdateBookShelf: (id: number, action: string) => void
  goToEbookSeries: () => void
  totalEbookSeriesPublish: number
}

function CoverMenuSection({
  chapterTotal,
  viewCount,
  commentCount,
  isEpub,
  isPdf,
  readNowHandler,
  isBookOnShelf,
  handleLikeBook,
  handleUpdateBookShelf,
  isBookShelfActionLoading,
  id,
  isLiked,
  likeCount,
  shareUrl,
  isLodingToggleBookLike,
  totalEbookSeriesPublish,
  goToEbookSeries,
}: CoverMenuSectionProps) {
  const { debounce } = useDebounce()

  return (
    <>
      <div className='flex flex-row space-x-[20px] tablet:space-x-[33px] mt-[80px] tablet:mt-[40px] mobile:mt-[20px]'>
        <div className='flex mobile:flex-col mobile:items-center'>
          <ListIcon className='text-primary' width='24' height='24' />
          <Box.Name className='dark:text-dark-10 mx-[5px] mobile:text-[12px]'>
            {addComma(chapterTotal)}
          </Box.Name>
        </div>

        <div className='border-l-[1px] h-[24px] mobile:h-auto border-gray-3 dark:border-purple' />

        <div className='flex mobile:flex-col mobile:items-center'>
          <EyeIcon className='text-primary' width='24' height='24' />
          <Box.Name className='dark:text-dark-10 mx-[5px] mobile:text-[12px]'>
            {abbreviateNumber(viewCount)}
          </Box.Name>
        </div>

        <div className='border-l-[1px] h-[24px] mobile:h-auto border-gray-3 dark:border-purple' />

        <div className='flex mobile:flex-col mobile:items-center'>
          <ChatboxIcon className='text-primary' width='24' height='24' />
          <Box.Name className='dark:text-dark-10 mx-[5px] mobile:text-[12px]'>
            {abbreviateNumber(commentCount)}
          </Box.Name>
        </div>

        {isEpub && (
          <>
            <div className='border-l h-[24px] mobile:h-auto border-gray-3 dark:border-purple' />
            <div className='flex mobile:flex-col mobile:items-center'>
              <EBookIcon className='text-primary' width='24' height='24' />
              <Box.Name className='dark:text-dark-10 mx-[5px] mobile:text-[12px]'>
                EPUB
              </Box.Name>
              <Box.Label className='mobile:text-[12px]'>ไฟล์</Box.Label>
            </div>
          </>
        )}

        {isPdf && (
          <>
            <div className='border-l h-[24px] mobile:h-auto border-gray-3 dark:border-purple' />
            <div className='flex mobile:flex-col mobile:items-center'>
              <EBookIcon className='text-primary' width='24' height='24' />
              <Box.Name className='mx-[5px] mobile:text-[14px] dark:text-dark-10'>
                PDF
              </Box.Name>
              <Box.Label className='mobile:text-[12px] dark:text-dark-10'>
                ไฟล์
              </Box.Label>
            </div>
          </>
        )}
      </div>
      <div className='flex border-b border-gray-3 dark:border-purple my-[30px] mobile:my-[20px]' />
      <div className='font-mitr flex flex-row mobile:flex-wrap-reverse mobile:space-y-[20px] space-x-[20px] mobile:hidden'>
        <Button
          type='button'
          className='bg-primary dark:bg-purple w-[114px] mobile:w-[164px] tablet:w-[190px]'
          onClick={readNowHandler}
          disabled={chapterTotal === 0}
        >
          <EyeIcon
            width='24'
            height='24'
            className='text-white dark:text-white-2 mr-[5px]'
          />
          <span className='font-mitr text-white dark:text-white-2 font-medium text-sm'>
            อ่านเลย
          </span>
        </Button>
        {Boolean(totalEbookSeriesPublish) && SHOW_EBOOK_FEATURE && (
          <Button
            type='button'
            onClick={goToEbookSeries}
            className='bg-primary dark:bg-purple w-[145px] mobile:w-[164px] tablet:w-[190px]'
          >
            <EBookIcon className='text-white mr-[5px]' />
            <span className='text-white font-medium text-sm'>
              เรื่องนี้มีอีบุ๊ก
            </span>
          </Button>
        )}
        {!isBookOnShelf ? (
          <Button
            disabled={isBookShelfActionLoading}
            data-test='add-book-shelf'
            onClick={() =>
              debounce(() => handleUpdateBookShelf(id, 'ADD'), 400)
            }
            type='button'
            className='bg-gray-4 dark:bg-dark-7 dark:border dark:border-dark-10 cursot-pointer tablet-mobile:w-[144px]'
          >
            <AddIcon
              data-test='add-book-shelf-icon'
              width='24'
              height='24'
              className='text-secondary dark:text-dark-10 mr-[5px]'
            />
            <span className='text-secondary dark:text-dark-10 font-medium text-sm'>
              เพิ่มเข้าชั้น
            </span>
          </Button>
        ) : (
          <Button
            disabled={isBookShelfActionLoading}
            data-test='remove-book-shelf'
            type='button'
            onClick={() =>
              debounce(() => handleUpdateBookShelf(id, 'REMOVE'), 400)
            }
            className='bg-blue-6 dark:bg-dark-7 dark:border dark:border-primary cursor-pointer tablet-mobile:w-[144px]'
          >
            <CheckIcon
              data-test='remove-book-shelf-icon'
              width='24'
              height='24'
              className='text-primary mr-[5px]'
            />
            <span className='text-primary font-medium text-sm'>
              เพิ่มเข้าชั้น
            </span>
          </Button>
        )}
        <Button
          type='button'
          className='border border-gray-4 dark:border-dark-10 mobile:w-[124px]'
          disabled={isLodingToggleBookLike}
          onClick={handleLikeBook}
        >
          {isLiked ? (
            <HeartSolidIcon
              width='24'
              height='24'
              className='text-red mr-[5px]'
            />
          ) : (
            <HeartIcon
              width='24'
              height='24'
              className='text-secondary dark:text-dark-10 mr-[5px]'
            />
          )}
          <span className='text-secondary dark:text-dark-10 font-medium text-sm'>
            {abbreviateNumber(likeCount)}
          </span>
        </Button>
        <SocialSharing shareUrl={shareUrl} type='story' bookId={id} />
      </div>
      <div className='font-mitr flex mb-[20px] space-x-[20px] desktop-tablet:hidden'>
        {!isBookOnShelf ? (
          <Button
            disabled={isBookShelfActionLoading}
            onClick={() => handleUpdateBookShelf(id, 'ADD')}
            type='button'
            className='bg-gray-4 dark:bg-transparent dark:border dark:border-dark-10 cursot-pointer mobile:w-[144px]'
          >
            <AddIcon
              width='24'
              height='24'
              className='text-secondary dark:text-dark-10 mr-[5px]'
            />
            <span className='text-secondary dark:text-dark-10 font-medium text-sm'>
              เพิ่มเข้าชั้น
            </span>
          </Button>
        ) : (
          <Button
            disabled={isBookShelfActionLoading}
            type='button'
            onClick={() => handleUpdateBookShelf(id, 'REMOVE')}
            className='bg-blue-6 dark:bg-transparent dark:border dark:border-primary cursor-pointer mobile:w-[144px]'
          >
            <CheckIcon
              width='24'
              height='24'
              className='text-primary mr-[5px]'
            />
            <span className='text-primary font-medium text-sm'>
              เพิ่มเข้าชั้น
            </span>
          </Button>
        )}
        <Button
          type='button'
          className='border border-gray-4 dark:border-dark-10 mobile:w-[124px]'
          onClick={handleLikeBook}
        >
          {isLiked ? (
            <HeartSolidIcon
              width='24'
              height='24'
              className='text-red mr-[5px]'
            />
          ) : (
            <HeartIcon
              width='24'
              height='24'
              className='text-secondary dark:text-dark-10 mr-[5px]'
            />
          )}
          <span className='text-secondary dark:text-dark-10 font-medium text-sm'>
            {abbreviateNumber(likeCount)}
          </span>
        </Button>
        <SocialSharing shareUrl={shareUrl} type='story' bookId={id} />
      </div>
      <div className='font-mitr mobile:grid mobile:grid-cols-2 mobile:gap-y-[20px] gap-x-[20px] desktop-tablet:hidden'>
        <Button
          type='button'
          className='bg-primary dark:bg-purple mobile:w-[164px] tablet:w-[190px]'
          onClick={readNowHandler}
          disabled={chapterTotal === 0}
        >
          <EyeIcon
            width='24'
            height='24'
            className='text-white dark:text-dark-10 mr-[5px]'
          />
          <span className='font-mitr text-white dark:text-white-2 font-medium text-sm'>
            อ่านเลย
          </span>
        </Button>
        {Boolean(totalEbookSeriesPublish) && SHOW_EBOOK_FEATURE && (
          <Button
            type='button'
            onClick={goToEbookSeries}
            className='bg-primary dark:bg-purple mobile:w-[164px] tablet:w-[190px]'
          >
            <EBookIcon className='text-white mr-[5px]' />
            <span className='text-white font-medium text-sm'>
              เรื่องนี้มีอีบุ๊ก
            </span>
          </Button>
        )}
      </div>
    </>
  )
}

export default CoverMenuSection
